import React, { memo } from "react";
import { useDispatch } from "react-redux";
import { ReactComponent as CheckmarkIcon } from "../../assets/checkmark.svg";
import { ReactComponent as CheckmarkIconFilled } from "../../assets/checkmark-icon.svg";
import { ReactComponent as TimeIcon } from "../../assets/time.svg";
import { ReactComponent as DispatchIcon } from "../../assets/dispatch-icon.svg";

import { fetchReleaseMissionStop } from "Slices/stationConfirmation";
import { DEPLOYED_MISSION_LIST_MODAL, toggleModal } from "Slices/modals";

import {
  ListItemStyled,
  ListItemContentStyled,
  ListItemMainContentStyled,
  InfoSectionStyled,
  VerticalSeparatorStyled,
  MissionStatusStyled,
  MissionStatusIconContainerStyled,
  MissionStatusIconBgStyled,
  MissionStatusIconStyled,
  StatusBadgeStyled,
  StatusBadgeColumnStyled,
  MissionNameStyled,
  ActionButtonStyled,
  ButtonGroupStyled,
  ButtonTextStyled,
  ButtonLabelStyled,
  ButtonIconStyled,
  ListItemDividerStyled,
} from "../../styles";
import { VehicleInfo } from "../VehicleInfo/VehicleInfo";

// ============================================================================
// Style Constants
// ============================================================================

type MissionStatus = "delivery" | "ready";

const MISSION_STATUS_BG_COLORS: Record<MissionStatus, string> = {
  delivery: "#F0E0D1",
  ready: "#94D4E3",
};

const getMissionStatusIcon = (status: MissionStatus): React.ReactNode => {
  switch (status) {
    case "delivery":
      return <TimeIcon />;
    case "ready":
      return <CheckmarkIcon />;
    default:
      return null;
  }
};

// ============================================================================
// Types
// ============================================================================

export interface ActionRequiredListItemData {
  id: string;
  name: string;
  imageUrl?: string;
  batteryPercent: number; // 0-100 (raw percentage from vehicle.batteryLevel)
  missionStatus: MissionStatus; // delivery = "Delivery to Dock X", ready = "Ready for Work"
  missionText: string;
  missionName?: string;
  lowBattery?: boolean; // DEV-4157: battery below the mission-assignment threshold
  buttonType: "release" | "dispatch";
  buttonText: string;
  queuedMissionId: string;
  vehicleId: string;
  stationId?: string;
  fleetId?: string;
  // DEV-3706: when true the vehicle is offline AND this is a dispatch row —
  // the backend (DEV-3686) rejects dispatching a mission to it, so the primary
  // dispatch button is disabled and the row shows the "Offline" treatment.
  isOffline?: boolean;
  // DEV-3706: raw offline state regardless of button type. A release row's
  // secondary "Change mission" (re-dispatch) button gates on this so an offline
  // vehicle awaiting release still can't be re-dispatched; the primary "Release
  // Vehicle" action is intentionally left enabled.
  vehicleOffline?: boolean;
}

interface ActionRequiredListItemProps {
  vehicle: ActionRequiredListItemData;
  isLast?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const ActionRequiredListItem = memo<ActionRequiredListItemProps>(
  ({ vehicle, isLast = false }) => {
    const {
      name,
      imageUrl,
      missionStatus,
      missionText,
      missionName,
      buttonText,
    } = vehicle;
    const dispatch = useDispatch();

    // Get style configuration based on mission status
    const statusBgColor = MISSION_STATUS_BG_COLORS[missionStatus];
    const statusIcon = getMissionStatusIcon(missionStatus);

    // Highlight the badge when the vehicle has actionable status:
    //   "Ready for Work"           → green pill + checkmark
    //   "Manual Release Required"  → amber pill + clock (operator must walk
    //                                 to the vehicle to release it before it
    //                                 will move on to the next mission)
    //   low battery (DEV-4157)     → red pill, no "Ready for Work" status, since
    //                                 the vehicle can't be assigned a mission
    const isOffline = vehicle.isOffline ?? false;
    // Raw offline state — gates the secondary "Change mission" re-dispatch on a
    // release row (where isOffline stays false because the release action is
    // still valid). Falls back to isOffline for dispatch rows.
    const vehicleOffline = vehicle.vehicleOffline ?? isOffline;
    const isLowBattery = !!vehicle.lowBattery;
    const normalizedText = missionText.toLowerCase();
    const isReadyForWork = !isLowBattery && normalizedText === "ready for work";
    const isManualReleaseRequired =
      normalizedText === "manual release required";
    const badgeVariant:
      | "ready"
      | "release"
      | "offline"
      | "warning"
      | undefined = isOffline
      ? "offline"
      : isLowBattery
      ? "warning"
      : isReadyForWork
      ? "ready"
      : isManualReleaseRequired
      ? "release"
      : undefined;

    const handleReleaseClick = () => {
      dispatch(
        fetchReleaseMissionStop({
          queuedMissionId: vehicle.queuedMissionId,
          successMessage: "Vehicle released successfully",
        })
      );
    };

    const handleDispatchClick = () => {
      // DEV-3706: never open the dispatch modal for an offline vehicle — the
      // backend (DEV-3686) rejects the assignment, producing a silent mission
      // failure. Covers both the primary dispatch button (isOffline) and the
      // release row's secondary "Change mission" re-dispatch (vehicleOffline).
      // The buttons are also rendered `disabled`; this is a defensive guard in
      // case a click still fires.
      if (isOffline || vehicleOffline) return;
      dispatch(
        toggleModal({
          type: DEPLOYED_MISSION_LIST_MODAL,
          data: {
            vehicleId: vehicle.vehicleId,
            stationId: vehicle.stationId,
            fleetId: vehicle.fleetId,
          },
        })
      );
    };

    return (
      <ListItemStyled
        $isLast={isLast}
        data-test-id={`action-required-list-item-${vehicle.id}`}
      >
        <ListItemContentStyled>
          <ListItemMainContentStyled>
            <VehicleInfo
              name={name}
              imageUrl={imageUrl}
              batteryPercent={vehicle.batteryPercent}
            />

            <InfoSectionStyled>
              <VerticalSeparatorStyled />

              <MissionStatusStyled
                data-test-id={`action-required-mission-status-${vehicle.id}`}
              >
                <MissionStatusIconContainerStyled>
                  <MissionStatusIconBgStyled $bgColor={statusBgColor} />
                  <MissionStatusIconStyled>
                    {statusIcon}
                  </MissionStatusIconStyled>
                </MissionStatusIconContainerStyled>
                <StatusBadgeColumnStyled>
                  <StatusBadgeStyled
                    $variant={badgeVariant}
                    data-test-id={`action-required-mission-text-${vehicle.id}`}
                  >
                    {isReadyForWork && (
                      <CheckmarkIconFilled aria-hidden="true" />
                    )}
                    {isManualReleaseRequired && <TimeIcon aria-hidden="true" />}
                    {missionText}
                  </StatusBadgeStyled>
                  {missionName && (
                    <MissionNameStyled
                      data-test-id={`action-required-mission-name-${vehicle.id}`}
                    >
                      {missionName}
                    </MissionNameStyled>
                  )}
                </StatusBadgeColumnStyled>
              </MissionStatusStyled>
            </InfoSectionStyled>
          </ListItemMainContentStyled>

          {vehicle.buttonType === "release" ? (
            <ButtonGroupStyled>
              <ActionButtonStyled
                $small
                data-test-id={`action-required-redispatch-${vehicle.id}`}
                data-button-type="dispatch"
                onClick={handleDispatchClick}
                disabled={vehicleOffline}
                $disabled={vehicleOffline}
                title={
                  vehicleOffline
                    ? "Vehicle is offline — cannot dispatch"
                    : "Change mission"
                }
              >
                <ButtonIconStyled>
                  <DispatchIcon />
                </ButtonIconStyled>
              </ActionButtonStyled>
              <ActionButtonStyled
                $variant="primary"
                data-test-id={`action-required-button-${vehicle.id}`}
                data-button-type="release"
                onClick={handleReleaseClick}
              >
                <ButtonTextStyled>
                  <ButtonLabelStyled>{buttonText}</ButtonLabelStyled>
                </ButtonTextStyled>
              </ActionButtonStyled>
            </ButtonGroupStyled>
          ) : (
            <ActionButtonStyled
              data-test-id={`action-required-button-${vehicle.id}`}
              data-button-type="dispatch"
              onClick={handleDispatchClick}
              disabled={isOffline}
              $disabled={isOffline}
              title={
                isOffline ? "Vehicle is offline — cannot dispatch" : undefined
              }
            >
              <ButtonTextStyled>
                <ButtonIconStyled>
                  <DispatchIcon />
                </ButtonIconStyled>
                <ButtonLabelStyled>{buttonText}</ButtonLabelStyled>
              </ButtonTextStyled>
            </ActionButtonStyled>
          )}
        </ListItemContentStyled>
        {!isLast && <ListItemDividerStyled />}
      </ListItemStyled>
    );
  }
);

ActionRequiredListItem.displayName = "ActionRequiredListItem";
